import React from 'react';
import { FiCheckCircle, FiClock, FiHelpCircle, FiUserCheck, FiFilter } from 'react-icons/fi';

// Two-way acceptance of an assessment. The backend returns reviewerAccepted /
// userAccepted / userRejected on each shared-assessment row (see AcceptanceStatusDto);
// this collapses them into a single pill.
export const ACCEPTANCE_STYLES = {
  'Accepted': 'bg-[#0077b5]/10 text-[#0077b5] border border-[#0077b5]/30',
  'Need Info': 'bg-[#F47F2E]/10 text-[#F47F2E] border border-[#F47F2E]/30',
  'Partly Accepted': 'bg-[#F47F2E]/10 text-[#F47F2E] border border-[#F47F2E]/30',
  'Not Accepted': 'bg-[#919191]/10 text-[#919191] border border-[#919191]/30',
};

export const ACCEPTANCE_ICONS = {
  'Accepted': FiCheckCircle,
  'Need Info': FiHelpCircle,
  'Partly Accepted': FiUserCheck,
  'Not Accepted': FiClock,
};

export const ACCEPTANCE_FILTERS = ['All', 'Accepted', 'Partly Accepted', 'Need Info', 'Not Accepted'];

// row -> { key, label, title }. `key` drives both styling and the filter pills;
// `label` is what the badge shows (partial acceptance names the side that accepted).
export const getAcceptanceState = (row = {}) => {
  const reviewerAccepted = !!row.reviewerAccepted;
  const userAccepted = !!row.userAccepted;
  const userRejected = !!row.userRejected;

  if (userRejected) {
    return { key: 'Need Info', label: 'Need Info', title: 'The customer has requested more information' };
  }
  if (reviewerAccepted && userAccepted) {
    return { key: 'Accepted', label: 'Accepted', title: 'Accepted by both the reviewer and the customer' };
  }
  if (reviewerAccepted) {
    return { key: 'Partly Accepted', label: 'Reviewer Only', title: 'Accepted by the reviewer — awaiting customer acceptance' };
  }
  if (userAccepted) {
    return { key: 'Partly Accepted', label: 'Customer Only', title: 'Accepted by the customer — awaiting reviewer acceptance' };
  }
  return { key: 'Not Accepted', label: 'Not Accepted', title: 'Not accepted yet' };
};

// Fixed-width pill so every badge in a table column lines up
const AcceptanceBadge = ({ row }) => {
  const { key, label, title } = getAcceptanceState(row);
  const Icon = ACCEPTANCE_ICONS[key];
  return (
    <span
      title={title}
      className={`inline-flex items-center justify-center gap-1.5 w-[128px] px-2 py-1 rounded-full text-[11px] font-semibold leading-none ${ACCEPTANCE_STYLES[key]}`}
    >
      <Icon className="text-[13px] flex-shrink-0" />
      <span>{label}</span>
    </span>
  );
};

// Pill group for filtering a table by acceptance state. When `counts` is supplied
// (option -> number), each pill shows how many rows it would match instead of an icon.
export const AcceptanceFilter = ({ value, onChange, label = 'Acceptance', counts }) => (
  <div className="flex items-center gap-1.5 min-w-0">
    <span
      className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#53545a] flex-shrink-0"
      title={label}
    >
      <FiFilter className="text-[#919191] text-xs flex-shrink-0" />
      <span className="whitespace-nowrap">{label}</span>
    </span>
    <span className="h-3.5 w-px bg-[#919191]/30 flex-shrink-0" aria-hidden="true" />
    <div className="flex items-center gap-1">
      {ACCEPTANCE_FILTERS.map(option => {
        const active = value === option;
        const Icon = ACCEPTANCE_ICONS[option];
        return (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            title={option}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border transition-colors whitespace-nowrap flex-shrink-0 ${
              active
                ? 'bg-[#0077b5] text-[#ffffff] border-[#0077b5]'
                : 'bg-[#ffffff] text-[#919191] border-[#919191]/30 hover:text-[#53545a] hover:border-[#919191]/60'
            }`}
          >
            {counts ? (
              <span
                className={`inline-flex items-center justify-center min-w-[16px] px-0.5 rounded-full text-[10px] font-bold leading-[14px] ${
                  active ? 'bg-[#ffffff]/25 text-[#ffffff]' : 'bg-[#919191]/15 text-[#53545a]'
                }`}
              >
                {counts[option] || 0}
              </span>
            ) : (
              Icon && <Icon className="text-[12px]" />
            )}
            {option}
          </button>
        );
      })}
    </div>
  </div>
);

export default AcceptanceBadge;
